import axios, { AxiosError, AxiosRequestConfig, type InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '../models/api-response';
import { ApiError } from '../models/api-error';
import { getFromStorage, setInStorage } from './storage';
import { ACCESS_TOKEN } from '@/constants';
import { firebaseService } from './firebase';

axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (!config.headers['Content-Type']) {
            config.headers.set('Content-Type', 'application/json');
        }

        if (!config.headers.Authorization) {
            const accessToken = getFromStorage(ACCESS_TOKEN);
            if (accessToken) {
                config.headers.set('Authorization', `Bearer ${accessToken}`);
            }
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    },
);

const getErrorMessage = (error: AxiosError): string => {
    const { response, message } = error;
    if (response?.data && (response.data as ApiError).message) {
        return (response.data as ApiError).message;
    }
    return response?.statusText || message || 'HTTP request failed';
};

let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string | null> | null = null;

const refreshToken = async (): Promise<string | null> => {
    if (isRefreshingToken && tokenRefreshPromise) {
        return tokenRefreshPromise;
    }
    isRefreshingToken = true;
    const authTokenPromise = firebaseService.getAuthToken();
    if (!authTokenPromise) {
        isRefreshingToken = false;
        return null;
    }
    tokenRefreshPromise = authTokenPromise.finally(() => {
        isRefreshingToken = false;
        tokenRefreshPromise = null;
    });
    return tokenRefreshPromise;
};

export const callApi = async <T>(config: AxiosRequestConfig, retries = 2): Promise<ApiResponse<T>> => {
    try {
        const response: AxiosResponse = await axios(config);

        return {
            status: 'success',
            data: response.data,
            message: response.data?.message,
            error: null,
        };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;

            if (axiosError.response?.status === 401) {
                try {
                    if (retries > 0) {
                        const newToken = await refreshToken();
                        if (newToken) {
                            setInStorage(ACCESS_TOKEN, newToken);
                            config.headers = {
                                ...config.headers,
                                Authorization: `Bearer ${newToken}`,
                            };
                            return callApi<T>(config, retries - 1);
                        } else {
                            await firebaseService.logOut();
                            window.location.href = '/login';
                        }
                    }
                } catch {
                    await firebaseService.logOut();
                    window.location.href = '/login';
                }
            }

            return {
                status: 'error',
                data: null,
                error: {
                    message: getErrorMessage(axiosError),
                },
            };
        }

        return {
            status: 'error',
            data: null,
            error: {
                message: (error as Error).message,
            },
        };
    }
};
